import { DateTime } from 'luxon';

import { showErrorsInline } from './action-button.js';

/**
 * A copy button. A refused copy shows on the button itself ("Kopiera
 * manuellt"), next to the text to select, never as a toast.
 */
export function integrationClipboard() {
    return {
        copied: false,
        copying: false,
        failed: false,

        async copy(value) {
            this.copied = false;
            this.failed = false;
            this.copying = true;

            try {
                await navigator.clipboard.writeText(value);
                this.copied = true;
            } catch {
                this.failed = true;
            } finally {
                this.copying = false;
            }
        },
    };
}

document.addEventListener('alpine:init', () => {
    window.Alpine.data('integrationClipboard', integrationClipboard);
    window.Alpine.data('integrationExpiry', integrationExpiry);
    window.Alpine.data('sftpPanel', sftpPanel);
});

export function integrationExpiry() {
    return {
        /**
         * Whether each mode's server error may show: set when a submit comes
         * back with one, cleared when the admin picks another mode, so an
         * error for the date never lingers under Aldrig or Efter.
         */
        showErrors: { days: false, custom: false },
        _stopIntercepting: null,

        init() {
            this.$watch('expiryType', () => {
                this.showErrors = { days: false, custom: false };
            });
            this._stopIntercepting = this.$wire.interceptMessage('createToken', ({ onSuccess }) => {
                onSuccess(({ payload }) => {
                    const errors = payload?.snapshot?.memo?.errors ?? {};
                    this.showErrors = { days: 'expiryDays' in errors, custom: 'expiresAt' in errors };
                });
            });
        },

        destroy() {
            this._stopIntercepting?.();
        },

        get expiryType() {
            return this.$wire.expiryType;
        },
        /**
         * The date createToken() will store and name, on the server's UTC
         * calendar (as the token list shows it), not the browser's or
         * Stockholm's: past midnight in Stockholm a day count still lands on
         * the UTC day. A typed date is its own day either way.
         */
        get expiryLine() {
            if (this.expiryType === 'never') {
                return 'Upphör aldrig.';
            }

            const days = Number(this.$wire.expiryDays);
            const expiry = this.expiryType === 'days'
                ? (Number.isInteger(days) && days >= 1 && days <= 365
                    ? DateTime.utc().plus({ days }) : null)
                : DateTime.fromISO(this.$wire.expiresAt || '', { zone: 'utc' });

            if (!expiry?.isValid) {
                return '';
            }

            const months = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
            return `Upphör ${expiry.day} ${months[expiry.month - 1]} ${expiry.year}.`;
        },
    };
}

/**
 * Calls whose failure the panel writes next to what caused it: the details
 * lookup, the status dropdown and Uppdatera on the file list.
 */
export function isReportedInPanel(action) {
    return action.name === 'fileDetails'
        || action.name === 'setStatus'
        || (action.name === '$refresh' && action.metadata?.island?.name === 'files');
}

export function sftpPanel() {
    return {
        open: false,
        tab: 'overview',
        statusOpen: false,
        removeOpen: false,
        newPasswordOpen: false,
        filterOpen: false,
        periodOpen: false,
        details: null,
        detailsId: null,
        editingCancelled: false,
        observedStatus: undefined,
        retrying: false,
        refreshing: false,
        filesError: null,
        detailsError: null,
        statusError: null,

        init() {
            try {
                this.open = localStorage.getItem('sb-sftp-panel-open') === '1';
                this.tab = localStorage.getItem('sb-sftp-panel-tab') === 'files' ? 'files' : 'overview';
            } catch {}
            this.$watch('open', value => {
                try {
                    localStorage.setItem('sb-sftp-panel-open', value ? '1' : '0');
                } catch {}
            });
            this.$watch('tab', value => {
                try {
                    localStorage.setItem('sb-sftp-panel-tab', value);
                } catch {}
            });
            this.$wire.interceptMessage(({ message }) => {
                if (Array.from(message.actions).every(isReportedInPanel)) {
                    showErrorsInline(message);
                }
            });
        },

        /**
         * The panel's card. Called from a button, `$el` is that button.
         */
        card() {
            return this.$el.closest('.sb-integration-panel');
        },

        /**
         * The collapsed row: open the panel and bring it into focus.
         */
        openPanel() {
            this.open = true;
            this.$focusCard(this.card());
        },

        /**
         * "Aktivera SFTP" and "Redigera": the form comes back from the
         * server, so the panel is brought into focus once it has rendered.
         */
        async edit() {
            const card = this.card();
            this.editingCancelled = false;
            try {
                await this.$wire.startEditing();
            } catch {
                return;
            }
            this.$focusCard(card);
        },

        activate() {
            this.open = true;
            this.tab = 'overview';
            this.edit();
        },

        syncStatus(status) {
            const wasPending = ['pending', 'deleting'].includes(this.observedStatus);
            if (status !== this.observedStatus) {
                this.statusError = null;
            }
            this.observedStatus = status;
            if (wasPending && !['pending', 'deleting'].includes(status)) {
                this.$wire.$refresh();
            }
        },

        /**
         * The status dropdown. The status strip shows the change; the toast
         * confirms it, and only when the server says something changed.
         */
        async changeStatus(status) {
            this.statusOpen = false;
            this.statusError = null;
            try {
                if (await this.$wire.setStatus(status)) {
                    this.$dispatch('notify', { type: 'success', message: status === 'active' ? 'Kontot aktiverat' : 'Kontot pausat' });
                }
            } catch {
                this.statusError = 'Kunde inte ändra status. Försök igen.';
            }
        },

        cancelEdit(inactive = false) {
            this.editingCancelled = true;
            this.$wire.publicKeysText = '';
            this.$wire.allowedIpsText = '';
            this.$wire.passwordLogin = false;
            for (const field of [this.$refs.publicKeys, this.$refs.allowedIps]) {
                if (field) {
                    field.value = '';
                    field.defaultValue = '';
                    field.textContent = '';
                }
            }
            if (inactive) {
                this.open = false;
            }
            this.$wire.cancelEditing();
        },

        /**
         * "Uppdatera" on the Filer toolbar: one request that re-renders only
         * the file list, so uploads made while the page is open show up.
         */
        async refreshFiles() {
            if (this.refreshing) {
                return;
            }
            this.refreshing = true;
            this.filesError = null;
            try {
                await this.$wire.$island('files').$refresh();
            } catch {
                this.filesError = 'Kunde inte uppdatera fillistan. Försök igen.';
            } finally {
                this.refreshing = false;
            }
        },

        async openDetails(id) {
            this.detailsId = id;
            this.detailsError = null;
            this.filesError = null;
            try {
                const details = await this.$wire.fileDetails(id);
                if (this.detailsId === id) {
                    this.details = details;
                }
            } catch {
                if (this.detailsId === id) {
                    this.filesError = 'Kunde inte hämta filens detaljer. Försök igen.';
                }
            }
        },

        closeDetails() {
            this.details = null;
            this.detailsId = null;
            this.detailsError = null;
        },

        async retryDetails() {
            const id = this.details?.id;
            if (!id || this.retrying) {
                return;
            }
            this.retrying = true;
            this.detailsError = null;
            try {
                await this.$wire.$island('files').retryFile(id);
                const details = await this.$wire.fileDetails(id);
                if (this.details?.id === id) {
                    this.details = details;
                }
            } catch {
                if (this.details?.id === id) {
                    this.detailsError = 'Kunde inte kontrollera filen igen. Försök igen.';
                }
            } finally {
                this.retrying = false;
            }
        },
    };
}
